/**
 * Master Data Management (MDM) Types
 *
 * Purpose: TypeScript type definitions for MDM frontend components
 * Architecture: Comprehensive type system for all MDM operations and data structures
 * Dependencies: Base types from auth.ts
 * Author: Development Team
 * Created: 2025-01-27
 */

import { PermissionKey } from '../constants/permissionKeys';

// =============================================================================
// CORE MDM INTERFACES
// =============================================================================

/**
 * Source label for an alias row.
 * - 'global': alias.tenant_id === 'GLOBAL' (visible to every tenant on the parent value).
 * - 'local':  alias.tenant_id is the active tenant (private to that tenant).
 *
 * ORCH-115: aliases are deliberately not represented as 'overridden' — see
 * `enhancement-docs/ORCH-115-master-data-value-aliases.md` source vocabulary table.
 */
export type AliasSource = 'global' | 'local';

/**
 * Single alternate name on a master data value (ORCH-115).
 */
export interface MasterDataValueAlias {
  id: string;
  alias: string;
  source: AliasSource;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

/**
 * Body shape for live alias-duplicate pre-flight checks (ORCH-115).
 *
 * `collision_kind`:
 *   'name'                 — alias spelling matches another value's visible canonical name.
 *   'alias'                — matches an existing alias visible to the active tenant.
 *   'underlying_canonical' — parent value is OVERRIDDEN and the proposed spelling matches
 *                            the underlying GLOBAL canonical (would conflict on revert).
 */
export interface AliasCheckDuplicateResponse {
  is_duplicate: boolean;
  colliding_value_id: string | null;
  colliding_value_name: string | null;
  collision_kind: 'name' | 'alias' | 'underlying_canonical' | null;
}

/**
 * Where a list's order comes from for the active tenant (ORCH-383)
 */
export interface ValueOrderSourceResponse {
  source: 'tenant' | 'global' | 'alphabetical';
  can_reorder: boolean;
}

/**
 * Master Data Value - Core entity for MDM system.
 * Lists arrive in their display order (custom order, else A→Z); consumers filter,
 * never sort (ORCH-383).
 */
export interface MasterDataValue {
  id: string;
  fielddef_id: string;
  tenant_id: string;
  name: string;
  is_active: boolean;
  is_deleted: boolean;
  parent_value_id?: string | null; // Parent value ID for hierarchical fields
  global_value_id?: string;
  meta_data?: Record<string, any>; // Contains raw UUIDs for list field references (resolved via MDM cache)
  chip_color?: string | null; // Optional chip color key from valueChipColors (e.g., 'teal', 'blue', 'green')
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  deleted_at?: string;
  deleted_by?: string;
  scope_info: ScopeInfo;
  hierarchy_path?: HierarchyPathItem[]; // Full hierarchy path from root to this value
  // ORCH-115: aliases attached to the underlying value, resolved per the active tenant.
  // List endpoints cap at the first N (alphabetic case-insensitive) and set
  // `aliases_truncated = true`; clients needing the full set should call GET /values/{id}.
  aliases?: MasterDataValueAlias[];
  aliases_truncated?: boolean;
  // ORCH-332: false for an ancestor returned by a scoped (for_permission) list only to
  // keep the hierarchy navigable: show it, don't offer it for selection.
  selectable?: boolean;
}

/**
 * Hierarchy path item for displaying breadcrumb navigation
 */
export interface HierarchyPathItem {
  id: string;
  name: string;
  fielddef_id: string;
  tenant_id: string;
  scope?: 'GLOBAL' | 'LOCAL' | 'OVERRIDDEN';
}

/**
 * Scope information indicating value source and override status
 */
export interface ScopeInfo {
  scope: 'GLOBAL' | 'LOCAL' | 'OVERRIDDEN';
  source_tenant?: string;
  override_tenant?: string;
  can_edit: boolean;
  can_delete: boolean;
  hierarchy_level: number;
}

/**
 * Request payload for creating master data values
 */
export interface MasterDataValueCreate {
  fielddef_id: string;
  tenant_id: string;
  name: string;
  is_active?: boolean;
  parent_value_id?: string; // Parent value ID for hierarchical fields
  global_value_id?: string;
  meta_data?: Record<string, any>;
  // ORCH-115: optional list of alternate names to attach atomically with the
  // value. Each alias is created with the same tenant_id as the value.
  aliases?: string[];
}

/**
 * Request payload for updating master data values
 */
export interface MasterDataValueUpdate {
  name?: string;
  is_active?: boolean;
  parent_value_id?: string; // Updated parent value ID for hierarchical fields
  global_value_id?: string;
  meta_data?: Record<string, any>;
}

// =============================================================================
// IMPORT/EXPORT INTERFACES
// =============================================================================

/**
 * Import batch for tracking bulk operations
 */
export interface MasterDataImportBatch {
  id: string;
  fielddef_id: string;
  tenant_id: string;
  filename: string;
  total_rows: number;
  processed_rows: number;
  error_rows: number;
  status: ImportStatus;
  error_summary?: Record<string, any>;
  // ORCH-282: rows auto-resolved to a tenant override of a colliding GLOBAL value.
  overridden_from_global?: number;
  created_at: string;
  created_by: string;
  completed_at?: string;
  progress_percentage: number;
  success_rate: number;
}

/**
 * Import status enumeration
 */
export enum ImportStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * Export request payload
 */
export interface ExportRequest {
  fielddef_id: string;
  tenant_id: string;
  format: 'xlsx' | 'csv';
  include_global?: boolean;
}

/**
 * Import error information
 */
export interface ImportError {
  row: number;
  message: string;
  type: string;
}

// =============================================================================
// CONFLICT DETECTION INTERFACES
// =============================================================================

/**
 * Duplicate conflict types
 */
export enum ConflictType {
  DUPLICATE_ACTIVE = 'DUPLICATE_ACTIVE',
  DUPLICATE_INACTIVE = 'DUPLICATE_INACTIVE',
  DUPLICATE_DELETED = 'DUPLICATE_DELETED',
  // ORCH-282: name collides with an active GLOBAL value visible to this tenant.
  CROSS_SCOPE_GLOBAL = 'CROSS_SCOPE_GLOBAL',
}

/**
 * Conflict data for duplicate detection
 */
export interface ConflictData {
  conflict_type: ConflictType;
  existing_value_id: string;
  existing_value_name?: string;
  existing_global_value_id?: string;
  can_reactivate: boolean;
  // ORCH-282: set on CROSS_SCOPE_GLOBAL conflicts - the colliding value is a
  // GLOBAL row the tenant can create a tenant-scoped override of.
  can_override?: boolean;
  message: string;
  deleted_at?: string;
  is_deleted?: boolean;
  is_active?: boolean;
}

/**
 * Reactivation request payload
 */
export interface ReactivateValueRequest {
  update_fields?: Record<string, any>;
}

// =============================================================================
// TENANT CONTEXT INTERFACES
// =============================================================================

/**
 * Tenant context information
 */
export interface TenantContext {
  current_tenant_id: string;
  available_tenants: string[];
  can_access_global: boolean;
}

/**
 * Global value dependencies
 */
export interface GlobalValueDependencies {
  has_overrides: boolean;
  override_count: number;
  tenants_with_overrides: string[];
}

// =============================================================================
// WEBSOCKET INTERFACES
// =============================================================================

/**
 * WebSocket message types
 */
export enum WebSocketMessageType {
  VALUE_CREATED = 'VALUE_CREATED',
  VALUE_UPDATED = 'VALUE_UPDATED',
  VALUE_DELETED = 'VALUE_DELETED',
  IMPORT_PROGRESS = 'IMPORT_PROGRESS',
  CONTEXT_CHANGED = 'CONTEXT_CHANGED',
}

/**
 * WebSocket message structure
 */
export interface WebSocketMessage {
  type: WebSocketMessageType;
  data: any;
  timestamp: string;
  tenant_id: string;
  fielddef_id?: string;
}

// =============================================================================
// UI COMPONENT INTERFACES
// =============================================================================

/**
 * Filter options for value list
 */
export interface ValueListFilters {
  fielddef_id?: string;
  tenant_id?: string;
  parent_value_id?: string; // Filter by parent for hierarchical fields
  search?: string;
  scope?: 'GLOBAL' | 'LOCAL' | 'OVERRIDDEN' | 'ALL';
  include_inactive?: boolean;
  include_deleted?: boolean;
  // ORCH-332: only the values this permission reaches (pickers, MDM Manager)
  for_permission?: PermissionKey;
  // ORCH-366: a picker's offer request; apply every offer-time filter (needs fielddef_id)
  offer?: boolean;
  // ORCH-400: narrow to values matching what related fields hold; each entry is
  // "<fielddef_id>:<id>[,<id>...]" (needs fielddef_id)
  filter_by?: string[];
}

/**
 * Pagination state
 */
export interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
}

/**
 * Sort configuration
 */
export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * Value form state for create/edit operations
 */
export interface ValueFormState {
  name: string;
  is_active: boolean;
  global_value_id?: string;
  meta_data?: Record<string, any>;
}

/**
 * Validation errors for forms
 */
export interface ValidationErrors {
  name?: string;
  global_value_id?: string;
  meta_data?: string;
}

/**
 * Bulk operation result
 */
export interface BulkOperationResult {
  total_requested: number;
  successful: number;
  failed: number;
  errors: Array<{
    row?: number;
    message: string;
    type: string;
    data?: any;
  }>;
  created_values?: string[];
  updated_values?: string[];
  // ORCH-282: rows auto-resolved to a tenant override of a colliding GLOBAL value.
  overridden_from_global?: number;
}

// =============================================================================
// FIELD DEFINITION INTERFACES (FROM METADATA SERVICE)
// =============================================================================

/**
 * Field definition for MDM context
 */
export interface FieldDefinition {
  fielddef_id: string;
  name: string;
  data_type: string;
  description?: string;
  is_required: boolean;
  validation_rules?: Record<string, any>;
  tenant_id: string;
  status: 'active' | 'inactive';
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T = any> {
  status: 'success' | 'error';
  data?: T;
  message?: string;
  code?: string;
}

/**
 * API error response
 */
export interface ApiError {
  message: string;
  code?: string;
  details?: any;
  timestamp?: string;
}

/**
 * File upload response
 */
export interface FileUploadResponse {
  batch_id: string;
  message: string;
  status: string;
  parsing_errors?: number;
}

// =============================================================================
// COMPONENT PROP INTERFACES
// =============================================================================

/**
 * Props for MDM Manager page component
 */
export interface MDMManagerProps {
  initialFieldDefId?: string;
  initialTenantId?: string;
}

/**
 * Props for Value List component
 */
export interface ValueListProps {
  fielddef_id: string;
  tenant_id?: string;
  onValueSelect?: (value: MasterDataValue) => void;
  onValueCreate?: () => void;
  onValueEdit?: (value: MasterDataValue) => void;
  onValueDelete?: (value: MasterDataValue) => void;
  showActions?: boolean;
  selectable?: boolean;
}

/**
 * Props for Value Form component
 */
export interface ValueFormProps {
  value?: MasterDataValue;
  fielddef_id: string;
  tenant_id: string;
  isEdit?: boolean;
  onSubmit: (data: MasterDataValueCreate | MasterDataValueUpdate) => void;
  onCancel: () => void;
  loading?: boolean;
  errors?: ValidationErrors;
}

/**
 * Props for Import Modal component
 */
export interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  fielddef_id: string;
  tenant_id: string;
  onImportComplete?: (result: BulkOperationResult) => void;
}

/**
 * Props for Export Dialog component
 */
export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  fielddef_id: string;
  tenant_id: string;
  onExportComplete?: () => void;
}

/**
 * Props for Conflict Resolution Dialog
 */
export interface ConflictResolutionProps {
  open: boolean;
  onClose: () => void;
  conflict: ConflictData;
  onResolve: (action: 'reactivate' | 'create_new' | 'cancel') => void;
  loading?: boolean;
}

// =============================================================================
// HOOK INTERFACES
// =============================================================================

/**
 * Return type for MDM values query hook
 */
export interface UseMDMValuesReturn {
  data: MasterDataValue[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  hasMore: boolean;
  fetchMore: () => void;
}

/**
 * Return type for WebSocket hook
 */
export interface UseWebSocketReturn {
  connected: boolean;
  error: string | null;
  sendMessage: (message: any) => void;
  lastMessage: WebSocketMessage | null;
  connect: () => void;
  disconnect: () => void;
  subscribeToField: (fielddef_id: string) => void;
  unsubscribeFromField: (fielddef_id: string) => void;
  setCallbacks: (
    callbacks: Partial<import('../services/websocketService').WebSocketCallbacks>
  ) => void;
  isConnecting: boolean;
  connectionState: import('../services/websocketService').WebSocketState;
  currentTenant: string | null;
}

/**
 * Return type for import progress hook
 */
export interface UseImportProgressReturn {
  progress: MasterDataImportBatch | null;
  loading: boolean;
  error: string | null;
  startPolling: (batchId: string) => void;
  stopPolling: () => void;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Tenant selection option
 */
export interface TenantOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Field definition option
 */
export interface FieldDefinitionOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

/**
 * Breadcrumb item
 */
export interface BreadcrumbItem {
  label: string;
  href?: string;
  current?: boolean;
}

/**
 * Action button configuration
 */
export interface ActionButton {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  variant?: 'contained' | 'outlined' | 'text';
}
